import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getRouteMetadata } from '../config/routeSeo';

/**
 * PageSEO Component
 *
 * Automatically updates document title and meta description based on current route.
 * Runs on every route change via useLocation().
 *
 * Dynamic pages (like BlogPostPage) can override these values after they load data.
 */
export default function PageSEO() {
  const location = useLocation();

  useEffect(() => {
    const metadata = getRouteMetadata(location.pathname);

    // Update document title
    document.title = metadata.title;

    // Update or create meta description tag
    let metaDescription = document.querySelector('meta[name="description"]');

    if (!metaDescription) {
      // Create the meta tag if it doesn't exist
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }

    metaDescription.setAttribute('content', metadata.description);

    // Optional: Update Open Graph tags for better social sharing
    updateOrCreateMetaTag('og:title', metadata.title, 'property');
    updateOrCreateMetaTag('og:description', metadata.description, 'property');

    // Optional: Update Twitter Card tags
    updateOrCreateMetaTag('twitter:title', metadata.title, 'name');
    updateOrCreateMetaTag('twitter:description', metadata.description, 'name');
  }, [location.pathname]);

  return null; // This component doesn't render anything
}

/**
 * Helper function to update or create a meta tag
 */
function updateOrCreateMetaTag(
  property: string,
  content: string,
  attributeName: 'name' | 'property' = 'name'
) {
  const selector = `meta[${attributeName}="${property}"]`;
  let metaTag = document.querySelector(selector);

  if (!metaTag) {
    metaTag = document.createElement('meta');
    metaTag.setAttribute(attributeName, property);
    document.head.appendChild(metaTag);
  }

  metaTag.setAttribute('content', content);
}
